import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../App";
import { ArrowLeft, Lock, Crown, Wind, Eye, Smartphone } from "lucide-react";
import FuturaIcon from "../components/FuturaIcon";
import { useLanguage } from "../i18n/LanguageContext";
import { LanguageSelector } from "../components/LanguageSelector";
import { useIsWebView } from "../hooks/useIsWebView";
import BreathingGuide from "../components/tools/BreathingGuide";
import Grounding54321 from "../components/tools/Grounding54321";
import ScreenBreakChallenge from "../components/tools/ScreenBreakChallenge";

const PremiumLibrary = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const isWebView = useIsWebView();
  const [activeTool, setActiveTool] = useState(null);

  const plan = user?.plan || "descubre";
  const hasAccess = plan === "decide" || plan === "avanza";

  const tools = [
    {
      id: "breathing",
      icon: Wind,
      color: "#06b6d4",
      title: t("tool_breathing_title"),
      description: t("tool_breathing_card_desc"),
      duration: t("tool_breathing_duration"),
    },
    {
      id: "grounding",
      icon: Eye,
      color: "#8b5cf6",
      title: t("tool_grounding_title"),
      description: t("tool_grounding_card_desc"),
      duration: t("tool_grounding_duration"),
    },
    {
      id: "screenbreak",
      icon: Smartphone,
      color: "#10b981",
      title: t("tool_screenbreak_title"),
      description: t("tool_screenbreak_card_desc"),
      duration: t("tool_screenbreak_duration"),
    },
  ];

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="library-gate">
        <div className="fixed inset-0 bg-gradient-to-br from-[#0a0a0f] via-[#0d1020] to-[#0a0a0f] pointer-events-none" />
        <header className="relative z-20 border-b border-white/5">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2 text-white/60 hover:text-white"><ArrowLeft className="w-5 h-5" /><span className="font-medium">{t("nav_back")}</span></Link>
            <div className="flex items-center gap-3"><LanguageSelector variant="dark" /><div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#002FA7] to-[#5B21B6] flex items-center justify-center p-1"><FuturaIcon className="w-full h-full text-white" /></div></div>
          </div>
        </header>
        <main className="relative z-10 max-w-lg mx-auto px-6 py-24 text-center">
          <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-6"><Lock className="w-10 h-10 text-white/30" /></div>
          <h1 className="font-['Outfit'] text-2xl font-semibold mb-3">{t("lib_gate_title")}</h1>
          <p className="text-white/50 mb-8">{t("lib_gate_desc")}</p>
          {!isWebView && (
            <Button onClick={() => navigate("/pricing")} className="bg-gradient-to-r from-amber-500 to-orange-500 hover:opacity-90 text-black rounded-full px-8 py-3 font-medium" data-testid="library-gate-upgrade-btn">
              <Crown className="w-4 h-4 mr-2" />{t("lib_gate_btn")}
            </Button>
          )}
          {isWebView && (
            <p className="text-white/40 text-sm" data-testid="webview-sub-text-library">{t("webview_sub_managed")}</p>
          )}
        </main>
      </div>
    );
  }

  const ActiveComponent = activeTool === "breathing" ? BreathingGuide
    : activeTool === "grounding" ? Grounding54321
    : activeTool === "screenbreak" ? ScreenBreakChallenge
    : null;

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="premium-library-page">
      <div className="fixed inset-0 bg-gradient-to-br from-[#0a0a0f] via-[#0d1020] to-[#0a0a0f] pointer-events-none" />
      <header className="relative z-20 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-white/60 hover:text-white"><ArrowLeft className="w-5 h-5" /><span className="hidden sm:inline font-medium">{t("nav_back")}</span></Link>
          <div className="flex items-center gap-3">
            <LanguageSelector variant="dark" />
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#002FA7] to-[#5B21B6] flex items-center justify-center p-1"><FuturaIcon className="w-full h-full text-white" /></div>
            <span className="font-['Outfit'] font-semibold text-base sm:text-lg">FUTURA</span>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        <div className="mb-6 sm:mb-8 animate-fade-in">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs px-2 py-1 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20 font-medium">AVANZA</span>
          </div>
          <h1 className="font-['Outfit'] text-xl sm:text-3xl font-semibold tracking-tight mb-2">{t("lib_title")}</h1>
          <p className="text-white/50 text-sm">{t("lib_subtitle")}</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-fade-in" data-testid="tools-list">
          {tools.map((tool) => {
            const Icon = tool.icon;
            return (
              <button
                key={tool.id}
                onClick={() => setActiveTool(tool.id)}
                className="text-left bg-white/[0.04] border border-white/10 rounded-2xl p-5 hover:bg-white/[0.08] hover:border-white/20 transition-all group"
                data-testid={`tool-card-${tool.id}`}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110" style={{ backgroundColor: `${tool.color}20`, border: `1px solid ${tool.color}40` }}>
                  <Icon className="w-6 h-6" style={{ color: tool.color }} />
                </div>
                <h3 className="font-['Outfit'] font-semibold text-white text-base mb-1">{tool.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed mb-3">{tool.description}</p>
                <span className="text-xs uppercase tracking-wider" style={{ color: tool.color }}>{tool.duration}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-10 text-center">
          <p className="text-white/20 text-xs">{t("lib_disclaimer")}</p>
        </div>
      </main>

      {ActiveComponent && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={(e) => { if (e.target === e.currentTarget) setActiveTool(null); }} data-testid="tool-modal">
          <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <ActiveComponent onClose={() => setActiveTool(null)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default PremiumLibrary;
